import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Box, ActionIcon, Group } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useMediaQuery } from '@mantine/hooks'
import { GameCard } from './GameCard'
import { useDarkMode } from '../hooks/useDarkMode'
import type { GamePlatform } from '../types'

interface LowestPrice {
    amount?: number
    currency?: string
    priceDrop?: number
    percentageChange?: number
    store?: {
        id: string
        name: string
        country: string
    } | null
}

interface HorizontalGameScrollerProps {
    gamePlatforms: (GamePlatform & { lowestPrice?: LowestPrice | null })[]
}

export const HorizontalGameScroller: React.FC<HorizontalGameScrollerProps> = ({
    gamePlatforms,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const { colorScheme } = useDarkMode()

    // Use media queries to determine card width - same as homepage
    const isXS = useMediaQuery('(max-width: 430px)') // Extra small screens (phones in portrait)
    const isMobile = useMediaQuery('(max-width: 768px)')
    const isTablet = useMediaQuery('(max-width: 992px)')

    // Calculate card width based on screen size to match homepage grid
    // Homepage: base: 2, sm: 2, md: 3, lg: 4
    const getCardWidth = () => {
        if (isXS) {
            // XS screens: 1.5 cards side by side (66.67% width) for better readability
            return 'calc(50% - 0.5rem)'
        } else if (isMobile) {
            // Mobile: 2 cards side by side, accounting for container padding and gaps
            return 'calc(50% - 0.5rem)'
        } else if (isTablet) {
            // Tablet: 3 cards side by side
            return 'calc(33.333% - 0.67rem)'
        } else {
            // Desktop: 4 cards side by side
            return 'calc(25% - 0.75rem)'
        }
    }

    const checkScrollability = useCallback(() => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
            setCanScrollLeft(scrollLeft > 0)
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)

            // Calculate current page based on scroll position
            const cardsPerPage = isXS ? 1.5 : isMobile ? 2 : isTablet ? 3 : 4
            const totalPages = Math.ceil(gamePlatforms.length / cardsPerPage)
            const pageWidth = clientWidth
            const currentPageIndex = Math.round(scrollLeft / pageWidth)
            setCurrentPage(Math.min(currentPageIndex, totalPages - 1))
        }
    }, [isXS, isMobile, isTablet, gamePlatforms.length])

    const scrollLeft = () => {
        if (scrollRef.current) {
            const cardWidth = isXS ? scrollRef.current.clientWidth / 1.5 : isMobile ? scrollRef.current.clientWidth / 2 : scrollRef.current.clientWidth / 3
            scrollRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        if (scrollRef.current) {
            const cardWidth = isXS ? scrollRef.current.clientWidth / 1.5 : isMobile ? scrollRef.current.clientWidth / 2 : scrollRef.current.clientWidth / 3
            scrollRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' })
        }
    }

    const scrollToPage = (pageIndex: number) => {
        if (scrollRef.current) {
            const pageWidth = scrollRef.current.clientWidth
            const targetScrollLeft = pageIndex * pageWidth
            scrollRef.current.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })
        }
    }

    // Check scrollability on mount and when gamePlatforms change
    useEffect(() => {
        checkScrollability()
        const timeoutId = setTimeout(checkScrollability, 100) // Small delay to ensure DOM is updated
        return () => clearTimeout(timeoutId)
    }, [gamePlatforms, isMobile, isTablet, checkScrollability])

    // Check scrollability on resize
    useEffect(() => {
        const handleResize = () => checkScrollability()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [checkScrollability])

    const showControls = canScrollLeft || canScrollRight

    return (
        <Box style={{ position: 'relative' }}>
            {/* Left Arrow */}
            {showControls && (
                <ActionIcon
                    variant="filled"
                    size="lg"
                    style={{
                        position: 'absolute',
                        left: '-10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        opacity: canScrollLeft ? 1 : 0.3,
                        pointerEvents: canScrollLeft ? 'auto' : 'none',
                    }}
                    onClick={scrollLeft}
                >
                    <IconChevronLeft size={20} />
                </ActionIcon>
            )}

            {/* Right Arrow */}
            {showControls && (
                <ActionIcon
                    variant="filled"
                    size="lg"
                    style={{
                        position: 'absolute',
                        right: '-10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        opacity: canScrollRight ? 1 : 0.3,
                        pointerEvents: canScrollRight ? 'auto' : 'none',
                    }}
                    onClick={scrollRight}
                >
                    <IconChevronRight size={20} />
                </ActionIcon>
            )}

            {/* Scrollable Container */}
            <Box
                ref={scrollRef}
                onScroll={checkScrollability}
                style={{
                    display: 'flex',
                    gap: '1.5rem',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    paddingBottom: '1rem',
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                }}
                className="hide-scrollbar"
            >
                {gamePlatforms.map((gamePlatform) => (
                    <Box
                        key={`${gamePlatform.gameId}-${gamePlatform.platformId}`}
                        style={{
                            minWidth: getCardWidth(),
                            maxWidth: getCardWidth(),
                            flexShrink: 0,
                        }}
                    >
                        <GameCard
                            gamePlatform={gamePlatform}
                        />
                    </Box>
                ))}
            </Box>

            {/* Scroll Indicators */}
            {showControls && (
                <Group justify="center" mt="xs">
                    {Array.from({ length: Math.ceil(gamePlatforms.length / (isXS ? 1.5 : isMobile ? 2 : isTablet ? 3 : 4)) }).map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => scrollToPage(index)}
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: index === currentPage
                                    ? 'var(--mantine-color-customGreen-6)'
                                    : colorScheme === 'dark'
                                        ? 'var(--mantine-color-customGreen-8)'
                                        : 'var(--mantine-color-customGreen-1)',
                                transition: 'background-color 0.2s ease, transform 0.2s ease',
                                cursor: 'pointer',
                                transform: index === currentPage ? 'scale(1.2)' : 'scale(1)',
                            }}
                            onMouseEnter={(e) => {
                                if (index !== currentPage) {
                                    e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                                        ? 'var(--mantine-color-customGreen-4)'
                                        : 'var(--mantine-color-customGreen-2)'
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (index !== currentPage) {
                                    e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                                        ? 'var(--mantine-color-customGreen-3)'
                                        : 'var(--mantine-color-customGreen-1)'
                                }
                            }}
                        />
                    ))}
                </Group>
            )}
        </Box>
    )
} 